import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import api from '../utils/axiosConfig'

export interface PestTypeOption {
    value: string
    label: string
}

const requests = new Map<string, Promise<PestTypeOption[]>>()
const modelRawClassRequests = new Map<string, Promise<PestTypeOption[]>>()

const normalizeProjectIds = (projectIds: number[]): number[] =>
    [...new Set(projectIds.filter(Number.isInteger))].sort((left, right) => left - right)

const buildCacheKey = (projectIds: number[]): string =>
    JSON.stringify(normalizeProjectIds(projectIds))

export const getPestTypeOptions = async (
    projectIds: number[] = []
): Promise<PestTypeOption[]> => {
    const normalizedProjectIds = normalizeProjectIds(projectIds)
    const cacheKey = buildCacheKey(normalizedProjectIds)

    const pendingRequest = requests.get(cacheKey)
    if (pendingRequest) {
        return pendingRequest
    }

    const request = api
        .get<PestTypeOption[]>('/pest-type-options', {
            params: {
                projectIds:
                    normalizedProjectIds.length > 0 ? normalizedProjectIds.join(',') : undefined
            }
        })
        .then((response) => {
            return response.data
        })
        .finally(() => {
            requests.delete(cacheKey)
        })
    requests.set(cacheKey, request)
    return request
}

export const getModelRawClassFilterOptions = async (
    projectIds: number[] = []
): Promise<PestTypeOption[]> => {
    const normalizedProjectIds = normalizeProjectIds(projectIds)
    const cacheKey = buildCacheKey(normalizedProjectIds)

    const pendingRequest = modelRawClassRequests.get(cacheKey)
    if (pendingRequest) {
        return pendingRequest
    }

    const request = api
        .get<PestTypeOption[]>('/pest-type-options/model-raw-classes', {
            params: {
                projectIds:
                    normalizedProjectIds.length > 0 ? normalizedProjectIds.join(',') : undefined
            }
        })
        .then((response) => {
            return response.data
        })
        .finally(() => {
            modelRawClassRequests.delete(cacheKey)
        })
    modelRawClassRequests.set(cacheKey, request)
    return request
}

export const clearPestTypeOptionsCache = (): void => {
    requests.clear()
    modelRawClassRequests.clear()
}

export const usePestTypeOptions = (projectIds: number[] = [], enabled = true) => {
    const projectIdsKey = buildCacheKey(projectIds)
    const normalizedProjectIds = useMemo<number[]>(
        () => JSON.parse(projectIdsKey) as number[],
        [projectIdsKey]
    )
    const [options, setOptions] = useState<PestTypeOption[]>([])
    const [loading, setLoading] = useState(enabled)
    const requestIdRef = useRef(0)

    const fetchOptions = useCallback(
        async () => {
            if (!enabled) {
                setOptions([])
                setLoading(false)
                return []
            }
            const requestId = ++requestIdRef.current
            setLoading(true)
            try {
                const nextOptions = await getPestTypeOptions(normalizedProjectIds)
                if (requestId === requestIdRef.current) {
                    setOptions(nextOptions)
                }
                return nextOptions
            } catch (error) {
                console.error('获取蚊虫种类选项失败:', error)
                return null
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false)
                }
            }
        },
        [enabled, normalizedProjectIds]
    )

    useEffect(() => {
        setOptions([])
        void fetchOptions()
        return () => {
            requestIdRef.current += 1
        }
    }, [fetchOptions])

    return { options, loading, refreshOptions: fetchOptions }
}

export const useModelRawClassFilterOptions = (
    projectIds: number[] = [],
    enabled = true
) => {
    const projectIdsKey = buildCacheKey(projectIds)
    const normalizedProjectIds = useMemo<number[]>(
        () => JSON.parse(projectIdsKey) as number[],
        [projectIdsKey]
    )
    const [options, setOptions] = useState<PestTypeOption[]>([])
    const [loading, setLoading] = useState(enabled)
    const requestIdRef = useRef(0)

    const fetchOptions = useCallback(
        async () => {
            if (!enabled) {
                setOptions([])
                setLoading(false)
                return []
            }
            const requestId = ++requestIdRef.current
            setLoading(true)
            try {
                const nextOptions = await getModelRawClassFilterOptions(
                    normalizedProjectIds
                )
                if (requestId === requestIdRef.current) {
                    setOptions(nextOptions)
                }
                return nextOptions
            } catch (error) {
                console.error('获取模型识别类型选项失败:', error)
                return null
            } finally {
                if (requestId === requestIdRef.current) {
                    setLoading(false)
                }
            }
        },
        [enabled, normalizedProjectIds]
    )

    useEffect(() => {
        setOptions([])
        void fetchOptions()
        return () => {
            requestIdRef.current += 1
        }
    }, [fetchOptions])

    return { options, loading, refreshOptions: fetchOptions }
}
